import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Paperclip } from "lucide-react";
import { fmtDateTime } from "@/lib/format";
import { useSupplier } from "@/context/SupplierContext";
import { InviteStatusBadge, EnquiryOpenBadge } from "@/lib/enquiries";

export default function SupplierEnquiries() {
  const { selected, isAll } = useSupplier();
  const [invites, setInvites] = useState([]);
  const [now, setNow] = useState(Date.now());
  const navigate = useNavigate();

  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/seller/enquiries?buyer_org=${selected}`)
      .then((r) => setInvites(r.data.invites))
      .catch(() => toast.error("Failed to load enquiries"));
  }, [selected]);

  return (
    <div data-testid="seller-enquiries-page" className="space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Enquiries</h1>
        <p className="text-sm text-muted-foreground">
          {invites.length} enquiry invite{invites.length === 1 ? "" : "s"} {isAll ? "across all buyer organizations" : "from selected buyer"}
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full text-sm" data-testid="seller-enquiries-table">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-3 font-medium">Enquiry #</th>
              {isAll && <th className="px-4 py-3 font-medium">Buyer</th>}
              <th className="px-4 py-3 font-medium">Material</th>
              <th className="px-4 py-3 font-medium text-right">Quantity</th>
              <th className="px-4 py-3 font-medium">Timer</th>
              <th className="px-4 py-3 font-medium text-center">Files</th>
              <th className="px-4 py-3 font-medium">Your Status</th>
              <th className="px-4 py-3 font-medium">Enquiry</th>
            </tr>
          </thead>
          <tbody>
            {invites.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground" data-testid="seller-enquiries-empty">
                No enquiry invites yet. When a buyer floats an enquiry to you, it will appear here.
              </td></tr>
            )}
            {invites.map((inv) => (
              <tr key={inv.id} data-testid={`seller-enquiry-row-${inv.enquiry.enquiry_number}`} onClick={() => navigate(`/seller/enquiries/${inv.id}`)}
                className="cursor-pointer border-b transition-colors last:border-0 hover:bg-slate-50">
                <td className="px-4 py-3 font-mono text-xs font-semibold">{inv.enquiry.enquiry_number}</td>
                {isAll && <td className="px-4 py-3 text-xs">{inv.buyer_org_name}</td>}
                <td className="px-4 py-3">
                  <p className="font-medium">{inv.enquiry.material_code}{(inv.enquiry.items?.length || 0) > 1 && <span className="ml-1 text-xs font-normal text-muted-foreground">+{inv.enquiry.items.length - 1} more</span>}</p>
                  <p className="max-w-52 truncate text-xs text-muted-foreground" title={inv.enquiry.description}>{inv.enquiry.description}</p>
                </td>
                <td className="px-4 py-3 text-right font-mono">{inv.enquiry.quantity} {inv.enquiry.uom}</td>
                <td className="px-4 py-3 text-xs" data-testid={`seller-timer-cell-${inv.enquiry.enquiry_number}`} title={fmtDateTime(inv.enquiry.deadline)}>
                  {(() => {
                    const rem = Math.max(0, Math.floor((new Date(inv.enquiry.deadline).getTime() - now) / 1000));
                    const d = Math.floor(rem / 86400), h = Math.floor((rem % 86400) / 3600), m = Math.floor((rem % 3600) / 60), s = rem % 60;
                    const pad = (n) => String(n).padStart(2, "0");
                    return rem <= 0
                      ? <span className="font-semibold text-[#991B1B]">Time expired</span>
                      : <span className={`font-mono font-medium tabular-nums ${rem < 3600 ? "text-[#991B1B]" : rem < 86400 ? "text-[#92400E]" : "text-slate-700"}`}>{pad(d)}d : {pad(h)}h : {pad(m)}m : {pad(s)}s</span>;
                  })()}
                </td>
                <td className="px-4 py-3 text-center">
                  {inv.attachment_count > 0 && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Paperclip className="h-3 w-3" />{inv.attachment_count}</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col items-start gap-1">
                    <InviteStatusBadge status={inv.status} />
                    {inv.enquiry.buyer_terms_conditions?.trim() && !inv.terms_accepted && !["declined", "removed", "participated"].includes(inv.status) && (
                      <span className="rounded-full border border-[#FDE68A] bg-[#FEF3C7] px-2 py-0.5 text-[10px] font-semibold text-[#92400E]" data-testid={`terms-required-${inv.id.slice(0, 8)}`}>Terms Acceptance Required</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3"><EnquiryOpenBadge passed={inv.enquiry.deadline_passed} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
